import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceLine, CartesianGrid,
} from 'recharts';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import toast from 'react-hot-toast';
import './Challenge.css';

const ASSETS = ['BTC','ETH','SOL','BNB','XRP','ADA','DOGE','AVAX'];
const BOT_TICK_MS = 6000;

// ── Custom tooltip for equity chart ──────────────────────────────────────
function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="chart-tooltip">
      <span>${Number(d.balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
      {d.pnl !== undefined && (
        <span style={{ color: d.pnl >= 0 ? 'var(--green)' : 'var(--red)', fontSize: 11 }}>
          {d.pnl >= 0 ? '+' : ''}{d.pnl.toFixed(2)}
        </span>
      )}
    </div>
  );
}

// ── Rule progress bar ─────────────────────────────────────────────────────
function RuleBar({ label, current, limit, color, inverse = false }) {
  const pct = Math.min(100, (Math.abs(current) / Math.abs(limit)) * 100);
  const barColor = pct >= 90 ? 'var(--red)' : pct >= 70 ? 'var(--yellow)' : color;
  const display  = inverse ? Math.max(0, current) : current;
  return (
    <div className="rule-bar">
      <div className="rule-bar-top">
        <span className="rb-label">{label}</span>
        <span className="rb-val mono" style={{ color: barColor }}>
          {display >= 0 ? '' : ''}{Math.abs(display).toFixed(2)}% / {limit}%
        </span>
      </div>
      <div className="progress-bar-wrap">
        <div className="progress-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
      </div>
    </div>
  );
}

// ── Phase progress stepper ────────────────────────────────────────────────
function PhaseHeader({ challenge }) {
  const is2 = challenge.challengeType === '2-step';
  const ph  = challenge.currentPhase;
  const st  = challenge.status;

  const phases = is2
    ? [
        { n: 1, label: 'Phase 1 — 10% Target', done: ph > 1 || st === 'passed', active: ph === 1 },
        { n: 2, label: 'Phase 2 — 5% Target',  done: st === 'passed',            active: ph === 2 || st === 'phase2' },
        { n: 3, label: 'Funded!',               done: st === 'passed',            active: false },
      ]
    : [
        { n: 1, label: 'Evaluation — 10% Target', done: st === 'passed', active: st === 'active' },
        { n: 2, label: 'Funded!',                 done: st === 'passed', active: false },
      ];

  return (
    <div className="phase-header card">
      <div className="ph-steps">
        {phases.map((p, i) => (
          <div key={p.n} className="ph-step-wrap">
            <div className={`ph-dot ${p.done ? 'done' : p.active ? 'active' : ''}`}>{p.done ? '✓' : p.n}</div>
            <span className={`ph-label ${p.active ? 'active' : p.done ? 'done-label' : ''}`}>{p.label}</span>
            {i < phases.length - 1 && <div className={`ph-line ${p.done ? 'done' : ''}`} />}
          </div>
        ))}
      </div>
      {st === 'phase2' && (
        <div className="ph-phase2-note">
          ✅ Phase 1 passed! Now complete Phase 2 to unlock your funded account.
        </div>
      )}
    </div>
  );
}

export default function Challenge() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(null);
  const [trades, setTrades]       = useState([]);
  const [equityCurve, setEquityCurve] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [botLoading, setBotLoading] = useState(false);
  const [tickWarning, setTickWarning] = useState(null);
  const tickRef = useRef(null);

  const loadChallenge = useCallback(async () => {
    try {
      const [cRes, tRes] = await Promise.all([
        api.get(`/challenges/${id}`),
        api.get(`/trades/challenge/${id}?limit=30`),
      ]);
      const c = cRes.data.challenge;
      setChallenge(c);
      const ts = tRes.data.trades;
      setTrades(ts);
      const curve = [
        { i: 0, balance: c.startingBalance, pnl: 0 },
        ...ts.slice().reverse().map((t, i) => ({ i: i + 1, balance: t.balanceAfter, pnl: t.pnl })),
      ];
      setEquityCurve(curve);
    } catch {
      toast.error('Failed to load challenge');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadChallenge(); }, [loadChallenge]);

  // Bot tick polling
  const runTick = useCallback(async () => {
    try {
      const { data } = await api.post(`/bot/tick/${id}`);
      const c = data.challenge;
      setChallenge(c);
      setTrades(prev => [data.trade, ...prev.slice(0, 49)]);
      setEquityCurve(prev => [...prev, { i: prev.length, balance: data.trade.balanceAfter, pnl: data.trade.pnl }].slice(-80));
      if (data.warning) setTickWarning(data.warning);
      else setTickWarning(null);

      if (data.result === 'pass' && c.status === 'phase2') {
        toast.success('✅ Phase 1 passed! Moving to Phase 2…');
        clearInterval(tickRef.current);
      } else if (data.result === 'pass' && c.status === 'passed') {
        toast.success('🏆 Challenge PASSED! You are now funded!');
        clearInterval(tickRef.current);
      } else if (data.result === 'fail') {
        toast.error(`❌ Challenge FAILED: ${c.failReason}`);
        clearInterval(tickRef.current);
      }
    } catch {}
  }, [id]);

  useEffect(() => {
    const isActive = ['active', 'phase2'].includes(challenge?.status);
    if (challenge?.botRunning && isActive) {
      tickRef.current = setInterval(runTick, BOT_TICK_MS);
    } else {
      clearInterval(tickRef.current);
    }
    return () => clearInterval(tickRef.current);
  }, [challenge?.botRunning, challenge?.status, runTick]);

  const startBot = async () => {
    setBotLoading(true);
    try {
      await api.post(`/bot/start/${id}`);
      setChallenge(prev => ({ ...prev, botRunning: true }));
      toast.success('Bot started!');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to start bot'); }
    finally { setBotLoading(false); }
  };

  const stopBot = async () => {
    setBotLoading(true);
    try {
      await api.post(`/bot/stop/${id}`);
      setChallenge(prev => ({ ...prev, botRunning: false }));
      toast('Bot stopped');
    } catch { toast.error('Failed to stop bot'); }
    finally { setBotLoading(false); }
  };

  const setAsset = async (asset) => {
    try {
      await api.put(`/challenges/${id}/asset`, { asset });
      setChallenge(prev => ({ ...prev, selectedAsset: asset }));
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to set asset'); }
  };

  const requestPayout = async () => {
    try {
      await api.post(`/challenges/${id}/payout-request`);
      toast.success('Sim payout request submitted!');
      setChallenge(prev => ({ ...prev, payoutRequested: true, payoutStatus: 'pending' }));
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  if (loading) return <div className="loading-screen">Loading challenge…</div>;
  if (!challenge) return null;

  const pnl      = challenge.currentBalance - challenge.startingBalance;
  const pnlPct   = (pnl / challenge.startingBalance) * 100;
  const target   = (challenge.startingBalance * challenge.rules.profitTargetPct) / 100;
  const profProg = Math.min(100, Math.max(0, (pnl / target) * 100));
  const drawdown = challenge.peakBalance
    ? ((challenge.peakBalance - challenge.currentBalance) / challenge.peakBalance * 100)
    : 0;
  const dailyLoss = challenge.dailyStartBalance
    ? ((challenge.dailyStartBalance - challenge.currentBalance) / challenge.startingBalance * 100)
    : 0;
  const daysProg  = Math.min(100, ((challenge.tradingDays || 0) / (challenge.rules.minTradingDays || 10)) * 100);
  const isActive  = ['active', 'phase2'].includes(challenge.status);
  const phaseLabel = challenge.status === 'phase2' ? 'Phase 2' : `Phase ${challenge.currentPhase}`;

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content challenge-main">
        {/* Phase stepper */}
        {challenge.challengeType === '2-step' && <PhaseHeader challenge={challenge} />}

        {/* Top header */}
        <div className="ch-header">
          <div>
            <h1>{challenge.plan?.name} <span className="ch-phase-tag">{phaseLabel}</span></h1>
            <div className="ch-meta">
              <span className={`badge badge-${['active','phase2'].includes(challenge.status) ? 'active' : challenge.status}`}>
                ● {challenge.status === 'phase2' ? 'Phase 2 Active' : challenge.status}
              </span>
              <span className="text-muted mono" style={{ fontSize: 12 }}>
                Started {new Date(challenge.startedAt).toLocaleDateString()}
              </span>
              {challenge.expiresAt && (
                <span className="text-muted" style={{ fontSize: 12 }}>
                  Expires {new Date(challenge.expiresAt).toLocaleDateString()}
                </span>
              )}
              <span className="ch-split-badge">
                {challenge.currentSplitPct || 70}% Split
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            {challenge.status === 'passed' && !challenge.payoutRequested && (
              <button className="btn btn-success" onClick={requestPayout}>
                💰 Request Sim Payout (USDT)
              </button>
            )}
            {challenge.payoutRequested && (
              <span className={`badge badge-${challenge.payoutStatus === 'approved' ? 'passed' : challenge.payoutStatus === 'denied' ? 'failed' : 'pending'}`}>
                Payout: {challenge.payoutStatus}
              </span>
            )}
            {challenge.status === 'failed' && (
              <div className="fail-pill">❌ {challenge.failReason}</div>
            )}
          </div>
        </div>

        {/* Min days warning */}
        {tickWarning && (
          <div className="min-days-warn">
            ⏳ {tickWarning}
          </div>
        )}

        {/* Stats grid */}
        <div className="ch-stats-grid">
          <div className="card ch-stat-card primary-stat">
            <span className="csc-label">Balance</span>
            <span className="csc-value mono">${challenge.currentBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            <span style={{ fontSize: 14, fontFamily: 'var(--font-mono)', fontWeight: 700, color: pnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
              {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)} ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)
            </span>
          </div>

          {/* Rules */}
          <div className="card ch-stat-card">
            <span className="csc-label" style={{ display: 'block', marginBottom: 14 }}>Challenge Rules</span>
            <RuleBar label="Profit Progress"  current={pnlPct}           limit={challenge.rules.profitTargetPct} color="var(--green)" />
            <RuleBar label="Daily Loss"       current={Math.max(0,dailyLoss)} limit={challenge.rules.maxDailyLossPct} color="var(--accent)" inverse />
            <RuleBar label="Max Drawdown"     current={Math.max(0,drawdown)}  limit={challenge.rules.maxDrawdownPct}  color="var(--yellow)" inverse />

            {/* Trading days */}
            <div className="rule-bar" style={{ marginTop: 4 }}>
              <div className="rule-bar-top">
                <span className="rb-label">Trading Days</span>
                <span className="rb-val mono" style={{ color: (challenge.tradingDays || 0) >= (challenge.rules.minTradingDays || 10) ? 'var(--green)' : 'var(--accent)' }}>
                  {challenge.tradingDays || 0} / {challenge.rules.minTradingDays || 10}
                </span>
              </div>
              <div className="progress-bar-wrap">
                <div className="progress-bar-fill" style={{ width: `${daysProg}%`, background: daysProg >= 100 ? 'var(--green)' : 'var(--accent)' }} />
              </div>
            </div>
          </div>

          {/* Quick stats */}
          <div className="card ch-stat-card">
            <span className="csc-label" style={{ display: 'block', marginBottom: 14 }}>Account Info</span>
            {[
              ['Starting Balance', `$${challenge.startingBalance.toLocaleString()}`,             null],
              ['Peak Balance',     `$${(challenge.peakBalance||0).toLocaleString(undefined,{minimumFractionDigits:2})}`, null],
              ['Total Trades',     challenge.tradeCount || 0,                                    null],
              ['Payout Split',     `${challenge.currentSplitPct || 70}%`,                       'var(--accent)'],
              ['Asset',            challenge.selectedAsset || 'BTC',                            'var(--gold)'],
            ].map(([k, v, c]) => (
              <div key={k} className="qa-row">
                <span>{k}</span>
                <span className="mono" style={{ color: c || 'var(--text-primary)', fontWeight: 700 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Equity curve */}
        <div className="card chart-card">
          <div className="chart-header">
            <span className="card-title">Equity Curve</span>
            <span className="chart-sim-note">Equity Curve</span>
          </div>
          {equityCurve.length > 2 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={equityCurve} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.03)" strokeDasharray="3 3" />
                <XAxis dataKey="i" hide />
                <YAxis
                  domain={['auto', 'auto']}
                  tickFormatter={v => `$${(v/1000).toFixed(0)}K`}
                  tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                  axisLine={false} tickLine={false}
                />
                <Tooltip content={<ChartTooltip />} />
                <ReferenceLine y={challenge.startingBalance} stroke="var(--border-bright)" strokeDasharray="4 4" />
                <ReferenceLine y={challenge.startingBalance * (1 + challenge.rules.profitTargetPct / 100)}
                  stroke="rgba(16,232,138,0.3)" strokeDasharray="4 4" />
                <Line type="monotone" dataKey="balance" stroke="var(--accent)" strokeWidth={2}
                  dot={false} activeDot={{ r: 4, fill: 'var(--accent)' }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="chart-empty">Start the bot to build your equity curve</div>
          )}
          <div className="chart-legend">
            <span style={{ color: 'var(--border-bright)' }}>— Start</span>
            <span style={{ color: 'rgba(16,232,138,0.6)' }}>— Target</span>
            <span style={{ color: 'var(--accent)' }}>— Equity</span>
          </div>
        </div>

        {/* Bot controls */}
        {isActive && (
          <div className="card bot-card">
            <div className="card-title">Trading Bot</div>
            <p className="text-secondary" style={{ fontSize: 13, marginBottom: 20 }}>
              Automated trading bot. Executes trades based on market conditions. Ticks every 6 seconds.
            </p>

            {/* Asset picker */}
            <div className="asset-row">
              <span className="asset-row-label">Asset:</span>
              <div className="asset-grid">
                {ASSETS.map(a => (
                  <button key={a}
                    className={`asset-btn ${challenge.selectedAsset === a ? 'active' : ''}`}
                    onClick={() => setAsset(a)}
                    disabled={challenge.botRunning}
                  >{a}</button>
                ))}
              </div>
            </div>

            {/* Start/Stop */}
            <div className="bot-actions">
              {!challenge.botRunning
                ? <button className="btn btn-success" onClick={startBot} disabled={botLoading}>{botLoading ? '…' : '▶ Start Bot'}</button>
                : <button className="btn btn-danger"  onClick={stopBot}  disabled={botLoading}>{botLoading ? '…' : '■ Stop Bot'}</button>
              }
              {challenge.botRunning && (
                <div className="bot-live-indicator">
                  <span className="blink">●</span>
                  Bot running · {challenge.selectedAsset} · sim trade every 6s
                </div>
              )}
              <span className="text-muted mono" style={{ fontSize: 12, marginLeft: 'auto' }}>
                {challenge.tradeCount} trades · {challenge.tradingDays || 0} days
              </span>
            </div>

            {/* Min days info */}
            {(challenge.tradingDays || 0) < (challenge.rules.minTradingDays || 10) && (
              <div className="min-days-info">
                📅 <strong>{(challenge.rules.minTradingDays || 10) - (challenge.tradingDays || 0)} more trading days</strong> needed even if profit target is hit
              </div>
            )}
          </div>
        )}

        {/* Trade history */}
        <div className="card trades-card">
          <div className="card-title">Trade History</div>
          {trades.length === 0 ? (
            <div className="chart-empty">No trades yet. Start the bot to begin.</div>
          ) : (
            <div className="trades-wrap">
              <table className="trades-tbl">
                <thead>
                  <tr><th>Asset</th><th>Side</th><th>Entry</th><th>Exit</th><th>Size</th><th>P&L</th><th>Signal</th><th>Time</th></tr>
                </thead>
                <tbody>
                  {trades.map(t => (
                    <tr key={t._id}>
                      <td><strong className="mono">{t.asset}</strong></td>
                      <td><span className={`side-pill ${t.side.toLowerCase()}`}>{t.side}</span></td>
                      <td className="mono">${t.entryPrice.toLocaleString()}</td>
                      <td className="mono">${t.exitPrice?.toLocaleString() ?? '—'}</td>
                      <td className="mono">${t.size.toFixed(0)}</td>
                      <td className="mono" style={{ color: t.pnl >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 700 }}>
                        {t.pnl >= 0 ? '+' : ''}{t.pnl.toFixed(2)}
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.signal}</td>
                      <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{new Date(t.createdAt).toLocaleTimeString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Back link */}
        <Link to="/dashboard" className="btn btn-outline btn-sm" style={{ marginTop: 8 }}>← Back to Dashboard</Link>
      </main>
    </div>
  );
}
